// Environment-based API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const REQUEST_TIMEOUT = 300000; // 300 seconds timeout

async function handleResponse(response) {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    console.error('API Error:', response.status, error);
    const errorMessage = error.detail || error.message || 'An API error occurred';
    
    // More specific error messages based on status codes
    if (response.status === 404) {
      throw new Error(errorMessage || 'Resource not found. Please check the request ID.');
    } else if (response.status === 500) {
      throw new Error(errorMessage || 'Server error. Please try again later.');
    } else if (response.status === 401) {
      throw new Error('Unauthorized. Please refresh the page.');
    } else if (response.status === 403) {
      throw new Error('Access denied. You do not have permission to perform this action.');
    }
    
    throw new Error(errorMessage);
  }
  return response.json();
}

// Fetch helper for endpoints that return text (e.g., XML)
async function apiFetchText(endpoint, options = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
  
  try {
    // Get token from localStorage for authorization
    const token = localStorage.getItem('authToken');
    
    const config = {
      ...options,
      signal: controller.signal,
      headers: {
        'Accept': 'application/xml, text/plain;q=0.9, */*;q=0.8',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      // Remove credentials completely - using localStorage instead
    };

    const response = await fetch(endpoint, config);
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error('API Error (text):', response.status, errorText);
      throw new Error(errorText || 'An API error occurred');
    }
    return response.text();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please check your connection and try again.');
    }
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your internet connection.');
    }
    throw error;
  }
}

async function apiFetch(endpoint, options = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
  
  try {
    // Get token from localStorage for authorization
    const token = localStorage.getItem('authToken');
    
    const config = {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      // Remove credentials completely - using localStorage instead
    };

    const response = await fetch(endpoint, config);
    clearTimeout(timeoutId);
    
    // If we get 401, clear tokens and let the user re-authenticate
    if (response.status === 401 && !endpoint.includes('/auth/login')) {
      console.log('API call returned 401, clearing tokens');
      localStorage.removeItem('authToken');
      localStorage.removeItem('tokenExpiry');
      // Don't redirect here, let the ProtectedRoute handle it
    }
    
    return handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please check your connection and try again.');
    }
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your internet connection.');
    }
    throw error;
  }
}

export const freightAPI = {

  // Get all freight requests with pagination
  getAllRequests: async (page = 1, pageSize = 10, filters = {}) => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    
    if (filters.search) params.append('search', filters.search);
    if (filters.status && filters.status !== 'all') params.append('front_status', filters.status);
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);
    
    // Date filtering
    if (filters.date) {
      params.append('date', filters.date);
    } else {
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
    }
    
    return apiFetch(`${API_BASE_URL}/freight-requests?${params.toString()}`);
  },

  // Get top 5 Requests
  getTop5Requests: async () => {
    return apiFetch(`${API_BASE_URL}/freight-requests/top/recent`);
  },

  // Get a specific freight request
  getRequest: async (requestId) => {
    return apiFetch(`${API_BASE_URL}/freight-requests/${requestId}`);
  },

  getRequestById: async (requestId) => {
    return apiFetch(`${API_BASE_URL}/freight-requests/details/${requestId}`);
  },

  // Update a freight request
  updateRequest: async (requestId, updates) => {
    return apiFetch(`${API_BASE_URL}/freight-requests/${requestId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  // Get requests by status
  getRequestsByStatus: async (status) => {
    return apiFetch(`${API_BASE_URL}/freight-requests/status/${status}`);
  },

  // Get requests by date range
  getRequestsByDateRange: async (startDate, endDate) => {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    return apiFetch(`${API_BASE_URL}/freight-requests/date-range?${params}`);
  },

  // Get requests by company
  getRequestsByCompany: async (company) => {
    return apiFetch(`${API_BASE_URL}/freight-requests/company/${company}`);
  },

  // Get analytics data with optional date range
  getAnalytics: async (days = 7) => {
    return apiFetch(`${API_BASE_URL}/analytics?days=${days}`);
  },

  // Get PDF attachments for a specific request
  getPdfAttachments: async (requestId) => {
    return apiFetch(`${API_BASE_URL}/freight-requests/${requestId}/pdf-attachments`);
  },
  // Generate XML and send to FTP
  generateXmlAndSendToFtp: async (requestId) => {
    return apiFetch(`${API_BASE_URL}/freight-requests/${requestId}/send-to-ftp`, {
      method: 'POST',
    });
  },

  // Generate XML (returns XML string) for a specific request
  generateRequestXml: async (requestId) => {
    return apiFetchText(`${API_BASE_URL}/freight-requests/${requestId}/generate-xml`, {
      method: 'GET',
      headers: {
        'Accept': 'application/xml',
      },
    });
  },

  // Generate Excel and send (presumably via email or other means)
  generateExcelAndSend: async (requestId) => {
    return apiFetch(`${API_BASE_URL}/freight-requests/${requestId}/generate-excel-and-send`, {
      method: 'POST',
    });
  },

  // Get PDF attachments (base64) for a specific request using new backend endpoint
  getEmailPdfAttachments: async (requestId) => {
    return apiFetch(`${API_BASE_URL}/email-attachments/${requestId}`);
  },

  // Enrichment API - updates confidence for a request (should be called only once)
  enrichRequest: async (requestId) => {
    return apiFetch(`${API_BASE_URL}/enrichment/${requestId}`);
  },

  // Mark email as unread for reprocessing
  markEmailUnread: async (requestId) => {
    return apiFetch(`${API_BASE_URL}/freight-requests/${requestId}/mark-email-unread`, {
      method: 'POST',
    });
  },

  reprocessRequest: async (requestId) => {
    return apiFetch(`${API_BASE_URL}/freight-requests/${requestId}/reprocess`, {
      method: 'POST',
    });
  },

  getReprocessChain: async (requestId) => {
    return apiFetch(`${API_BASE_URL}/freight-requests/${requestId}/reprocess-chain`);
  },

}; 

export const authAPI = {
  login: async (username, password, rememberMe = false) => {
    return apiFetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ username, password, rememberMe }),
    });
  },
  me: async () => {
    return apiFetch(`${API_BASE_URL}/auth/me`);
  },
  logout: async (accessToken) => {
    return apiFetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },
};

export const supplierAPI = {
  // Get all suppliers with pagination
  getAllSuppliers: async (page = 1, pageSize = 20) => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    return apiFetch(`${API_BASE_URL}/api/suppliers?${params.toString()}`);
  },

  // Get a specific supplier by ID
  getSupplierById: async (supplierId) => {
    return apiFetch(`${API_BASE_URL}/api/suppliers/${supplierId}`);
  },

  // Update a supplier
  updateSupplier: async (supplierId, updates) => {
    return apiFetch(`${API_BASE_URL}/api/suppliers/${supplierId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  // Delete a supplier
  deleteSupplier: async (supplierId) => {
    return apiFetch(`${API_BASE_URL}/api/suppliers/${supplierId}`, {
      method: 'DELETE',
    });
  },

  // Create/Process new supplier with documents and images
  processDocuments: async (formDataPayload) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    
    try {
      const token = localStorage.getItem('authToken');
      
      const config = {
        method: 'POST',
        body: formDataPayload,
        signal: controller.signal,
        headers: {
          // Don't set Content-Type - browser will set it with boundary for multipart/form-data
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      };

      const response = await fetch(`${API_BASE_URL}/api/process-documents`, config);
      clearTimeout(timeoutId);
      
      return handleResponse(response);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout. Please check your connection and try again.');
      }
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your internet connection.');
      }
      throw error;
    }
  },
};

export const poItemsAPI = {
  // Search PO items with filters and pagination
  searchPOItems: async (params = {}) => {
    const searchParams = new URLSearchParams();
    
    if (params.po_number) searchParams.append('po_number', params.po_number);
    if (params.item_code) searchParams.append('item_code', params.item_code);
    if (params.quantity) searchParams.append('quantity', params.quantity);
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.page_size) searchParams.append('page_size', params.page_size.toString());
    
    return apiFetch(`${API_BASE_URL}/po-items/search?${searchParams.toString()}`);
  },
};
